import { loadStripe } from '@stripe/stripe-js';
import { config, getApiUrl } from '../config';

const stripePromise = loadStripe(config.stripe.publicKey);

export const initiateCheckout = async () => {
  const stripe = await stripePromise;

  if (!stripe) {
    throw new Error('Stripe failed to initialize');
  }

  // Get cart items and ensure they have the required properties
  const cartItems = window.localStorage.getItem('cartItems')
    ? JSON.parse(window.localStorage.getItem('cartItems') || '[]')
    : [];

  if (!cartItems.length) {
    throw new Error('Cart is empty');
  }

  // Get email from localStorage
  const email = window.localStorage.getItem('checkoutEmail');
  if (!email) {
    throw new Error('Email is required for checkout');
  }

  // Get checkout data (shipping, tax, etc.) from localStorage
  const checkoutDataStr = window.localStorage.getItem('checkoutData');
  const checkoutData = checkoutDataStr ? JSON.parse(checkoutDataStr) : null;
  
  // Debug: Log checkout data to see what's being sent
  console.log('🔍 Checkout data from localStorage:', checkoutData);
  console.log('🔍 Shipping fee:', checkoutData?.shippingFee);
  console.log('🔍 Tax amount:', checkoutData?.taxAmount);

  // Validate that each item has the required properties
  interface CartItem {
    id: string;
    name: string;
    price: number;
    quantity?: number;
    image: string;
    imageUrl?: string; // For backward compatibility
    size: string;
  }

  const validatedItems = cartItems.map((item: CartItem) => ({
    id: item.id,
    name: item.name,
    price: item.price,
    quantity: item.quantity || 1,
    image: item.image || item.imageUrl || '',
    size: item.size,
  }));

  const response = await fetch(`${getApiUrl('/api/create-checkout-session')}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: validatedItems,
      email: email,
      shippingFee: checkoutData?.shippingFee || 0,
      taxAmount: checkoutData?.taxAmount || 0,
      shippingMethod: checkoutData?.shippingMethod || 'pickup',
      shippingAddress: checkoutData?.shippingAddress || null,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Server error:', errorData);
    throw new Error(errorData.error || 'Failed to create checkout session');
  }

  const session = await response.json();

  if (!session.id) {
    console.error('Invalid session response:', session);
    throw new Error('Invalid checkout session response');
  }

  // Use sessionId for Stripe redirect, but display the clean order number
  const sessionId = session.sessionId || session.id;
  
  const result = await stripe.redirectToCheckout({
    sessionId: sessionId,
  });

  if (result.error) {
    throw new Error(result.error.message);
  }
};
